/**
 * signedDocumentSource — откъде идва ТЕКСТЪТ в PDF-а на един подпис.
 *
 * Отговорът трябва да е „от подписа", а не „от декларацията", и точно това
 * досега се извеждаше НА МЯСТО, в два викащи, всеки със своя верига резерви:
 *
 * | | заглавие | съдържание |
 * |---|---|---|
 * | `generatePDF.ts` (теглене) | `[lang] ?? bg ?? en` | `[lang] ?? bg ?? en` |
 * | `signatureService.ts` (подписване) | `[lang] ?? bg ?? code` | `[lang] ?? bg ?? ''` |
 *
 * И двете четяха ЖИВАТА декларация. Родител, подписал версия 3 през септември,
 * теглеше документа през януари и получаваше текста на версия 5 с подписа си
 * от септември — документ, който твърди, че е приел нещо, което не е чел.
 *
 * 🔴 Защо това е ЕДИН модул, а не поправка на две места: документът вече носи
 * номера на подписаната версия (задача 16). Разминат ли се двата викащи,
 * единият изписва „Версия 3" над текста на версия 5 — а номерът придава
 * достоверност на грешката. Версия и текст в един документ описват един и същ
 * текст или документът лъже; това е инвариант, не стил, и инвариантите живеят
 * на едно място.
 *
 * ⚠️ Какво този модул НЕ прави: не сверява снимката с живия текст. Решено
 * предварително — версията е по-добрият посредник. Сверяване на съдържание би
 * отказвало валидни подписи заради нормализация на редовете и заместени
 * плейсхолдъри: клиентът снима текста СЛЕД `replaceSystemPlaceholders`, тоест
 * снимката и живият текст НЕ съвпадат буквално дори в момента на подписване.
 * Разликите не са изброими — форматът на датата по език и учебната година са
 * примери, не списък.
 */

import type { Language } from '../types';
import { signedVersionOf, signedVersionPublishedMillis } from './declarationVersion';
import type { SignedVersionFields } from './declarationVersion';

/** Езикът по подразбиране, когато подписът не носи `contentLanguage`. */
const DEFAULT_CONTENT_LANGUAGE: Language = 'bg';

/**
 * Минимумът от `Signature`. Приема и raw Firestore документ.
 *
 * Полетата на снимката са опционални и `string | null`, защото старите подписи
 * ги нямат — а точно това е случаят, който модулът трябва да различава.
 */
export interface SignedDocumentSignatureFields extends SignedVersionFields {
  legalBinding?: {
    contentLanguage?: string | null;
    selectedContentSnapshot?: string | null;
    germanContentSnapshot?: string | null;
    selectedTitleSnapshot?: string | null;
    germanTitleSnapshot?: string | null;
  } | null;
}

/**
 * Минимумът от `Declaration` — ползва се САМО като резерв, когато снимка няма.
 *
 * ⚠️ Нетипизирани речници нарочно: и двата викащи подават `DocumentData`, а
 * `Declaration.title` е `Record<Language, string>`, който Firestore не гарантира.
 */
export interface SignedDocumentDeclarationFields {
  code?: string | null;
  title?: Record<string, string | undefined> | null;
  content?: Record<string, string | undefined> | null;
}

export interface SignedDocumentSource {
  /** Заглавието на избрания език. */
  declarationTitle: string;
  /** Текстът на избрания език. */
  declarationContent: string;
  /** Немското заглавие — `undefined`, когато избраният език ВЕЧЕ е немски. */
  germanDeclarationTitle?: string;
  /** Немският текст — `undefined`, когато избраният език ВЕЧЕ е немски. */
  germanDeclarationContent?: string;
  /** Езикът, на който родителят е чел — от подписа, с резерв `bg`. */
  contentLanguage: Language;
  /**
   * Версията, която документът има право да ТВЪРДИ.
   *
   * 🔴 `undefined` и когато подписът НОСИ версия, но текстът не е снимен: номер
   * над чужд текст е по-лошо от липсващ номер (Р1, вариант А).
   */
  declarationVersion?: number;
  /** Кога е публикувана тази версия — millis, `null` при неизвестно. */
  declarationVersionPublishedMillis: number | null;
  /**
   * Показаният ТЕКСТ е живият, не подписаният — документът трябва да го признае
   * и да не твърди версия.
   */
  contentSnapshotMissing: boolean;
  /**
   * Показаното ЗАГЛАВИЕ е текущото, не подписаното.
   *
   * 🔴 Отделно от `contentSnapshotMissing` и НЕ потиска версията. Снимка на
   * заглавието не се е събирала до днес, тоест всеки заварен подпис я няма —
   * потиснеше ли тя версията, задача 16 щеше да е мъртва за всички 1434
   * съществуващи подписа. Лечението е изричен ред в документа (Р2).
   */
  titleSnapshotMissing: boolean;
}

/** Непразен низ след изрязване — празният низ значи „няма снимка", не „празен текст". */
function snapshotOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Първата непразна стойност от веригата резерви. */
function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return '';
}

function normalizeLanguage(raw: unknown): Language {
  return raw === 'bg' || raw === 'de' || raw === 'en' ? raw : DEFAULT_CONTENT_LANGUAGE;
}

/**
 * Заглавието на избрания език, взето от ЖИВАТА декларация.
 *
 * 🔴 Изнесено, защото има ДВАМА викащи на различни страни на мрежата: този
 * модул (резерв, когато снимка няма) и клиентът в момента на подписване
 * (`useSignModal` — той произвежда снимката). Преписана на клиента, веригата се
 * разминава тихо: подписът пази едно заглавие, документът рисува друго за същия
 * подпис, и разликата се вижда чак когато някой сравни двата екрана.
 *
 * Резервът стига до `code`, защото документ без заглавие е по-лош от документ с
 * машинен код в заглавието.
 */
export function liveDeclarationTitle(
  declaration: SignedDocumentDeclarationFields | null | undefined,
  language: Language,
): string {
  return firstNonEmpty(
    declaration?.title?.[language],
    declaration?.title?.bg,
    declaration?.title?.en,
    declaration?.code,
  );
}

/**
 * Немското заглавие от живата декларация — БЕЗ верига резерви.
 *
 * ⚠️ Нарочно различно от `liveDeclarationTitle`: немската секция съществува, за
 * да покаже правно обвързващата немска версия. Няма ли немско заглавие, падането
 * на българското би сложило български текст под надпис „Rechtsverbindliche
 * Fassung (Deutsch)" — точно обратното на това, което секцията твърди.
 */
export function liveGermanDeclarationTitle(
  declaration: SignedDocumentDeclarationFields | null | undefined,
): string {
  return firstNonEmpty(declaration?.title?.de);
}

/**
 * Текстът за документа на един подпис — от снимката, с живата декларация като
 * последен резерв.
 *
 * Правилото за версията в едно изречение: **документът твърди версия само
 * когато ЦЕЛИЯТ му текст идва от подписа.** Немската секция е част от този
 * текст — тя е правно обвързващата половина, — затова липсващата ѝ снимка
 * потиска версията точно както липсващата снимка на избрания език.
 *
 * ⚠️ Немската снимка се изисква САМО когато немска секция изобщо би се
 * нарисувала (избраният език не е немски И немски текст съществува). Иначе
 * всеки подпис на немски би губил версията си заради снимка, която няма къде
 * да се покаже.
 */
export function resolveSignedDocumentSource(
  signature: SignedDocumentSignatureFields | null | undefined,
  declaration: SignedDocumentDeclarationFields | null | undefined,
): SignedDocumentSource {
  const binding = signature?.legalBinding ?? null;
  const contentLanguage = normalizeLanguage(binding?.contentLanguage);

  const liveContent = declaration?.content ?? undefined;

  // ── Съдържание на избрания език ────────────────────────────────────────────
  const contentSnapshot = snapshotOrNull(binding?.selectedContentSnapshot);
  const declarationContent = contentSnapshot ?? firstNonEmpty(
    liveContent?.[contentLanguage],
    liveContent?.bg,
    liveContent?.en,
  );

  // ── Немската половина ──────────────────────────────────────────────────────
  // Рисува се само за родител, който НЕ е чел на немски; за него немският текст
  // е правно обвързващата версия и минава по същото правило като основния.
  const wantsGermanSection = contentLanguage !== 'de';
  const germanSnapshot = snapshotOrNull(binding?.germanContentSnapshot);
  const germanDeclarationContent = wantsGermanSection
    ? (germanSnapshot ?? firstNonEmpty(liveContent?.de)) || undefined
    : undefined;

  // ── Заглавия ───────────────────────────────────────────────────────────────
  // 🔴 Веригата резерви е ЕДНА за двата викащи. Дотук `generatePDF` падаше на
  // `title.en`, а `signatureService` — на `declaration.code`; тоест един и същ
  // подпис даваше два различни документа според това кой път го е произвел.
  // Обединената верига минава през `en` ПРЕДИ кода: човешко заглавие на чужд
  // език е по-четимо от машинен код.
  const titleSnapshot = snapshotOrNull(binding?.selectedTitleSnapshot);
  const declarationTitle = titleSnapshot ?? liveDeclarationTitle(declaration, contentLanguage);

  const germanTitleSnapshot = snapshotOrNull(binding?.germanTitleSnapshot);
  const germanDeclarationTitle = wantsGermanSection
    ? (germanTitleSnapshot ?? liveGermanDeclarationTitle(declaration)) || undefined
    : undefined;

  // ── Какво липсва ───────────────────────────────────────────────────────────
  const germanSectionWillRender = wantsGermanSection && !!germanDeclarationContent;
  const contentSnapshotMissing =
    contentSnapshot === null || (germanSectionWillRender && germanSnapshot === null);

  // Заглавието липсва, ако липсва която и да е от снимките, които документът
  // ще покаже — немското заглавие е отделен низ и се снима отделно.
  const titleSnapshotMissing =
    titleSnapshot === null ||
    (germanSectionWillRender && !!germanDeclarationTitle && germanTitleSnapshot === null);

  return {
    declarationTitle,
    declarationContent,
    germanDeclarationTitle,
    germanDeclarationContent,
    contentLanguage,
    declarationVersion: contentSnapshotMissing ? undefined : signedVersionOf(signature),
    declarationVersionPublishedMillis: contentSnapshotMissing
      ? null
      : signedVersionPublishedMillis(signature),
    contentSnapshotMissing,
    titleSnapshotMissing,
  };
}
